import * as React from 'react';

import { DescriptionPage_SelfServeConfiguration_AboutModule_SubsectionName as AboutModuleSubsectionName } from '__generated__/graphql-types';

import { useIsEnterpriseAdminAbleToAssignContent } from 'bundles/enterprise-admin/useIsEnterpriseAdminAbleToAssignContent';
import { showPathwaysXDP } from 'bundles/unified-common/utils/showPathways';
import ArticlesRecommendationsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ArticlesRecommendationsModuleRenderer';
import CareerCertificateModuleRenderer from 'bundles/unified-description-page-common/module-renderers/CareerCertificateModuleRenderer';
import CourseraPlusRecommendationsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/CourseraPlusRecommendationsAndArticlesModuleRenderer';
import DiscoveryRecommendationsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/DiscoveryRecommendationsModuleRenderer';
import LearnerIncentiveModuleRenderer from 'bundles/unified-description-page-common/module-renderers/LearnerIncentiveModuleRenderer';
import PageBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PageBannerModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/TestimonialsModuleRenderer';
import ThinBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ThinBannerModuleRenderer';
import ConsumerBreadcrumbsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/breadcrumbs/ConsumerBreadcrumbsModuleRenderer';
import CareerOutcomesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/career-outcomes/ConsumerProfessionalCertificateCareerOutcomesModuleRenderer';
import PathwaysCareerOutcomesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/career-outcomes/ConsumerProfessionalCertificatePathwaysCareerOutcomesModuleRenderer';
import ConsumerEligibleDegreesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/eligible-degrees/ConsumerEligibleDegreesModuleRenderer';
import FAQsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/faqs/FAQsModuleRenderer';
import ProductOverviewModuleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/ConsumerSpecializationProductOverviewModuleRenderer';
import SyllabusModuleRenderer from 'bundles/unified-description-page-common/module-renderers/syllabus/SpecializationSyllabusModuleRenderer';
import UpsellModuleRenderer from 'bundles/unified-description-page-common/module-renderers/upsell/ConsumerUpsellModuleRenderer';
import type {
  ConsumerSpecializationMarketingPageFieldsFragment,
  SelfServeConfigPageBannerModuleFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationMarketingPageQuery';
import {
  KeyInformationItemNames,
  ModuleNames,
  ProductDetailItemNames,
  UpsellCardNames,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import type {
  PageConfig,
  ProductOverviewModuleConfig,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import { getAudienceLanguageCode } from 'bundles/unified-description-page-common/utils/dubbingUtil';

type Props = {
  pageData: ConsumerSpecializationMarketingPageFieldsFragment;
  pageConfig?: PageConfig | null;
};

const Renderer = ({ pageData, pageConfig }: Props) => {
  const isEnterpriseAdminAbleToAssignContent = useIsEnterpriseAdminAbleToAssignContent({
    ssr: false,
  });

  const shouldShowPathwaysXDP = showPathwaysXDP(pageData.slug);

  if (!pageConfig || !pageConfig.modules.length) {
    return null;
  }

  const pageConfiguration = {
    ...pageConfig,
    modules: pageConfig.modules.flatMap((module) => {
      if (
        shouldShowPathwaysXDP &&
        (module.name === ModuleNames.CareerCertificate || module.name === ModuleNames.EligibleDegrees)
      ) {
        // Remove CareerCertificate and EligibleDegrees modules if Pathways XDP is enabled
        // because they are displayed in SyllabusModule
        return [];
      }

      return module;
    }),
  };

  const modulesToRender = pageConfiguration.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.PageBanner:
        return (
          <PageBannerModuleRenderer
            key={moduleConfig.name}
            config={moduleConfig as SelfServeConfigPageBannerModuleFragment}
            pageData={pageData}
          />
        );
      case ModuleNames.ProductOverview:
        return (
          <ProductOverviewModuleRenderer
            pageData={pageData}
            config={
              {
                ...moduleConfig,
                // visibility of hero language info depends on XDP product AI dubbing, user site language
                showHeroLanguageInfo:
                  pageData.dubbedLanguages?.length && getAudienceLanguageCode(pageData.dubbedLanguages),
                showAssignButton: isEnterpriseAdminAbleToAssignContent,
                showBackgroundImage: true,
                keyInfoModule: {
                  items: [
                    { name: KeyInformationItemNames.CourseCount },
                    { name: KeyInformationItemNames.Reviews },
                    { name: KeyInformationItemNames.Difficulty },
                    { name: KeyInformationItemNames.Duration },
                    { name: KeyInformationItemNames.Schedule },
                    { name: KeyInformationItemNames.Stackability },
                    { name: KeyInformationItemNames.EarnCertificate },
                  ],
                },
                aboutModule: {
                  subsections: [
                    { name: AboutModuleSubsectionName.LearningOutcomes },
                    { name: AboutModuleSubsectionName.Skills },
                    { name: AboutModuleSubsectionName.DetailsToKnow },
                  ],
                },
                productDetailItems: [
                  ProductDetailItemNames.ShareableCertificate,
                  ProductDetailItemNames.IndustryCertification,
                  ProductDetailItemNames.LanguagesWithMLInfo,
                  ProductDetailItemNames.RecentlyUpdated,
                ],
              } as ProductOverviewModuleConfig
            }
            key={moduleConfig.name}
          />
        );
      case ModuleNames.CareerOutcomes:
        if (shouldShowPathwaysXDP) {
          return <PathwaysCareerOutcomesModuleRenderer pageData={pageData} key={moduleConfig.name} />;
        }
        return <CareerOutcomesModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.CareerCertificate:
        return <CareerCertificateModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.LearnerIncentive:
        return <LearnerIncentiveModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Syllabus:
        return (
          <SyllabusModuleRenderer
            pageData={pageData}
            config={{ showPathwaysXDP: shouldShowPathwaysXDP }}
            key={moduleConfig.name}
          />
        );
      case ModuleNames.EligibleDegrees:
        return <ConsumerEligibleDegreesModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Testimonials:
        return <TestimonialsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.ArticlesRecommendations:
        return <ArticlesRecommendationsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Upsell:
        return (
          <UpsellModuleRenderer
            key={moduleConfig.name}
            config={{
              upsellCards: [
                UpsellCardNames.CourseraPlusUpsellCard,
                UpsellCardNames.DegreeUpsellCard,
                UpsellCardNames.EnterpriseUpsellCard,
              ],
            }}
          />
        );
      case ModuleNames.Faqs:
        return <FAQsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.ThinBanner:
        return <ThinBannerModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.Breadcrumbs:
        return <ConsumerBreadcrumbsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.DiscoveryRecommendations:
        return <DiscoveryRecommendationsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.CourseraPlusRecommendations:
        return <CourseraPlusRecommendationsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      default:
        return null;
    }
  });

  return <>{modulesToRender}</>;
};

export default Renderer;
